/**
 * CORREÇÃO DO DESENHO A MÃO LIVRE.
 *
 * Pedido do dono: "quero poder desenhar com o dedo no formato que eu quiser,
 * ainda que o setor tenha formato que fuja do padrão. E ao desenhar com o dedo
 * e soltar, o sistema corrigir o traçado para ficar reto e não torto. Ou se
 * for um círculo, corrigir a circunferência para ficar suave."
 *
 * Entra a lista crua de pontos do gesto (`pointermove`), sai UMA forma de
 * `forma.js` — a mesma para setor, subsetor e posto. Quatro passos, nesta
 * ordem, e o primeiro que reconhece o desenho manda:
 *
 *   1. SIMPLIFICAR: Ramer–Douglas–Peucker com tolerância proporcional ao
 *      tamanho do traço (2% da diagonal). Sem isso um contorno de 500 pontos
 *      viraria 500 vértices no banco e o mapa engasgaria ao arrastar.
 *   2. ELIPSE: traçado que fecha e cujos pontos ficam perto da elipse inscrita
 *      na caixa (erro médio < 12% do raio) vira `{tipo: "elipse"}` — uma
 *      circunferência suave no lugar da batata que o dedo fez.
 *   3. ENDIREITAR: une segmentos quase colineares e PRENDE cada segmento ao
 *      ângulo mais próximo entre 0°, 45° e 90° quando o desvio é pequeno
 *      (< 12°), em cadeia (o ponto seguinte é projetado na direção presa, por
 *      isso o contorno não se abre). Depois fecha o polígono cruzando a reta
 *      de entrada do último canto com a reta de fechamento do primeiro: é o
 *      que faz o último canto ficar exatamente reto.
 *   4. RETÂNGULO: quatro cantos com os quatro lados no eixo viram
 *      `{tipo: "retangulo"}`. Um losango (lados a 45°) NÃO vira retângulo —
 *      achatá-lo na caixa envolvente mudaria o desenho.
 *
 * Nunca devolve forma abaixo do lado mínimo nem polígono com menos de três
 * pontos: nesses casos (risco curto, toque, linha reta) devolve o retângulo
 * mínimo em volta do traço. Só devolve `null` quando não há nenhum ponto
 * legível — aí não houve gesto.
 *
 * Módulo puro: nada de DOM. Quem escuta o ponteiro converte para coordenadas
 * do mundo ANTES de chamar (a correção é feita na escala do mapa, senão a
 * tolerância de 12° mudaria com o zoom).
 */
import {
  LIMITES_FORMA,
  caixaDePontos,
  criarElipse,
  criarPoligono,
  criarRetangulo,
  distanciaAoSegmento,
  limparPontos,
} from "./forma";

export const TOLERANCIAS = Object.freeze({
  /** Simplificação: fração da diagonal da caixa do traço. */
  SIMPLIFICACAO: 0.02,
  /** Fechamento: distância entre o primeiro e o último ponto, em frações da diagonal. */
  FECHAMENTO: 0.18,
  /** Elipse: erro médio aceito, em frações do raio (raio normalizado = 1). */
  ELIPSE: 0.12,
  /** Segmentos com direção mais próxima que isto (em graus) viram um só. */
  COLINEAR_GRAUS: 10,
  /** Desvio máximo (em graus) para prender o segmento em 0°, 45° ou 90°. */
  ENCAIXE_GRAUS: 12,
  /** Quanto o canto de fechamento pode andar, em frações da diagonal. */
  FECHAMENTO_CANTO: 0.25,
});

// Menos pontos que isto não descrevem uma curva: um "triângulo" de 4 pontos
// passaria por elipse por acidente de média.
const PONTOS_MINIMOS_ELIPSE = 8;
const PASSO_ANGULO = 45;
const GRAUS = 180 / Math.PI;
// Depois de prender, dois segmentos na mesma direção são exatamente iguais;
// meio grau só absorve o arredondamento.
const COLINEAR_APOS_PRENDER = 0.5;
const CANTOS_RETANGULO = 4;
const DETERMINANTE_MINIMO = 1e-9;

const anguloDe = (a, b) => Math.atan2(b.y - a.y, b.x - a.x) * GRAUS;
const distancia = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

/** Diferença entre dois ângulos em graus, dobrada para o intervalo −180..180. */
function diferencaAngular(a, b) {
  let diferenca = (a - b) % 360;
  if (diferenca > 180) diferenca -= 360;
  if (diferenca < -180) diferenca += 360;
  return diferenca;
}

const diagonalDe = (caixa) => (caixa ? Math.hypot(caixa.largura, caixa.altura) : 0);

// ------------------------------------------------------------ simplificar

function reduzir(pontos, inicio, fim, tolerancia, saida) {
  let maior = 0;
  let escolhido = -1;
  for (let i = inicio + 1; i < fim; i += 1) {
    const afastamento = distanciaAoSegmento(pontos[i], pontos[inicio], pontos[fim]);
    if (afastamento > maior) {
      maior = afastamento;
      escolhido = i;
    }
  }
  if (maior <= tolerancia || escolhido < 0) return;
  reduzir(pontos, inicio, escolhido, tolerancia, saida);
  saida.push(pontos[escolhido]);
  reduzir(pontos, escolhido, fim, tolerancia, saida);
}

/**
 * Ramer–Douglas–Peucker: mantém as pontas e só os vértices que se afastam do
 * atalho mais que `tolerancia`. Tolerância ≤ 0 devolve os pontos limpos.
 */
export function simplificar(pontos, tolerancia) {
  const limpos = limparPontos(pontos);
  if (limpos.length <= 2 || !(tolerancia > 0)) return limpos;
  const meio = [];
  reduzir(limpos, 0, limpos.length - 1, tolerancia, meio);
  return [limpos[0], ...meio, limpos[limpos.length - 1]];
}

// --------------------------------------------------------------- elipse

/** O gesto voltou para perto de onde começou. */
export function tracadoFechado(pontos, { fechamento = TOLERANCIAS.FECHAMENTO } = {}) {
  const limpos = limparPontos(pontos);
  if (limpos.length < 3) return false;
  const diagonal = diagonalDe(caixaDePontos(limpos));
  if (diagonal <= 0) return false;
  return distancia(limpos[0], limpos[limpos.length - 1]) <= diagonal * fechamento;
}

/**
 * Elipse inscrita na caixa do traço e o quanto os pontos fogem dela:
 * `{centro, raios, erroMedio}` — `erroMedio` em frações do raio (0 = em cima
 * da elipse, 0,41 = os cantos de um quadrado). `null` sem caixa com área.
 */
export function ajustarElipse(pontos) {
  const limpos = limparPontos(pontos);
  const caixa = caixaDePontos(limpos);
  if (!caixa || caixa.largura <= 0 || caixa.altura <= 0) return null;
  const rx = caixa.largura / 2;
  const ry = caixa.altura / 2;
  const centro = { x: caixa.x + rx, y: caixa.y + ry };
  const soma = limpos.reduce(
    (total, p) => total + Math.abs(Math.hypot((p.x - centro.x) / rx, (p.y - centro.y) / ry) - 1),
    0
  );
  return { centro, raios: { rx, ry }, erroMedio: soma / limpos.length };
}

function pareceElipse(pontos, tolerancias) {
  if (pontos.length < PONTOS_MINIMOS_ELIPSE) return false;
  if (!tracadoFechado(pontos, { fechamento: tolerancias.FECHAMENTO })) return false;
  const ajuste = ajustarElipse(pontos);
  return Boolean(ajuste) && ajuste.erroMedio < tolerancias.ELIPSE;
}

// ----------------------------------------------------------- endireitar

/**
 * Vértices onde a direção quase não muda são ruído do dedo. Percorre o
 * contorno como fechado (o primeiro vértice também é testado) e nunca desce
 * abaixo de três pontos.
 */
export function unirColineares(pontos, graus = TOLERANCIAS.COLINEAR_GRAUS) {
  let atuais = [...pontos];
  let mudou = true;
  while (mudou && atuais.length > LIMITES_FORMA.PONTOS_MINIMO) {
    mudou = false;
    for (let i = 0; i < atuais.length; i += 1) {
      const anterior = atuais[(i - 1 + atuais.length) % atuais.length];
      const seguinte = atuais[(i + 1) % atuais.length];
      const desvio = diferencaAngular(anguloDe(anterior, atuais[i]), anguloDe(atuais[i], seguinte));
      if (Math.abs(desvio) <= graus) {
        atuais = atuais.filter((_, indice) => indice !== i);
        mudou = true;
        break;
      }
    }
  }
  return atuais;
}

function direcaoPresa(angulo, graus) {
  const alvo = Math.round(angulo / PASSO_ANGULO) * PASSO_ANGULO;
  const desvio = diferencaAngular(angulo, alvo);
  if (Math.abs(desvio) > graus) return null;
  return { alvo, desvio, radianos: alvo / GRAUS };
}

/**
 * `b` projetado na direção presa (0°, 45°, 90°…) saindo de `a`. Fora da
 * tolerância devolve `b` intacto — traço oblíquo de propósito continua
 * oblíquo.
 */
export function prenderSegmento(a, b, graus = TOLERANCIAS.ENCAIXE_GRAUS) {
  const presa = direcaoPresa(anguloDe(a, b), graus);
  if (!presa) return b;
  // Projeção (e não rotação): o lado mantém o comprimento que ele tem NA
  // direção certa, então um lado de 300 quase reto não vira 302.
  const comprimento = distancia(a, b) * Math.cos(presa.desvio / GRAUS);
  return {
    x: a.x + comprimento * Math.cos(presa.radianos),
    y: a.y + comprimento * Math.sin(presa.radianos),
  };
}

function interseccao(p1, d1, p2, d2) {
  const determinante = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(determinante) < DETERMINANTE_MINIMO) return null;
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / determinante;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

// O último canto vai para o cruzamento da reta que chega nele com a reta de
// fechamento presa: é isto que deixa o canto de fechamento exatamente reto,
// em vez de sobrar um dente de 3 px onde o dedo não voltou no lugar.
function fecharContorno(pontos, { graus, limiteDeslocamento }) {
  if (pontos.length < LIMITES_FORMA.PONTOS_MINIMO) return pontos;
  const ultimo = pontos[pontos.length - 1];
  const penultimo = pontos[pontos.length - 2];
  const primeiro = pontos[0];
  const fechamento = direcaoPresa(anguloDe(ultimo, primeiro), graus);
  if (!fechamento) return pontos;
  const entrada = { x: ultimo.x - penultimo.x, y: ultimo.y - penultimo.y };
  const cruzamento = interseccao(penultimo, entrada, primeiro, {
    x: Math.cos(fechamento.radianos),
    y: Math.sin(fechamento.radianos),
  });
  if (!cruzamento || distancia(cruzamento, ultimo) > limiteDeslocamento) return pontos;
  return [...pontos.slice(0, -1), cruzamento];
}

/**
 * Contorno endireitado: segmentos quase colineares unidos, cada segmento
 * preso ao ângulo mais próximo e o canto de fechamento resolvido.
 * `opcoes` = {tolerancias, diagonal} (a diagonal limita o quanto o canto de
 * fechamento pode andar).
 */
export function endireitar(pontos, { tolerancias = TOLERANCIAS, diagonal = 0 } = {}) {
  const limpos = limparPontos(pontos);
  if (limpos.length < LIMITES_FORMA.PONTOS_MINIMO) return limpos;
  const cantos = unirColineares(limpos, tolerancias.COLINEAR_GRAUS);
  const presos = cantos.reduce(
    (saida, atual, indice) =>
      indice === 0 ? [atual] : [...saida, prenderSegmento(saida[saida.length - 1], atual, tolerancias.ENCAIXE_GRAUS)],
    []
  );
  const fechados = fecharContorno(presos, {
    graus: tolerancias.ENCAIXE_GRAUS,
    limiteDeslocamento: diagonal * tolerancias.FECHAMENTO_CANTO,
  });
  return unirColineares(fechados, COLINEAR_APOS_PRENDER);
}

// ------------------------------------------------------------ retângulo

/**
 * Quatro cantos com os quatro lados no eixo (horizontal/vertical). Losango de
 * lados a 45° tem cantos retos e NÃO passa por aqui — virar retângulo mudaria
 * o desenho.
 */
export function pareceRetangulo(pontos, graus = TOLERANCIAS.ENCAIXE_GRAUS) {
  if (pontos.length !== CANTOS_RETANGULO) return false;
  return pontos.every((atual, i) => {
    const seguinte = pontos[(i + 1) % CANTOS_RETANGULO];
    const angulo = Math.abs(anguloDe(atual, seguinte));
    const noEixo = Math.min(angulo, Math.abs(angulo - 90), Math.abs(angulo - 180));
    return noEixo <= graus;
  });
}

// --------------------------------------------------------------- entrada

/**
 * Forma corrigida a partir dos pontos crus do gesto.
 *
 * `opcoes.tolerancias` sobrescreve pontualmente `TOLERANCIAS` (a tela de
 * calibração e os testes usam isso; o app usa o padrão).
 *
 * `opcoes.extensaoMinima` (em unidades do MUNDO) descarta o gesto que mal saiu
 * do lugar: um toque trêmulo produz três ou quatro pontos e viraria uma sala
 * de 20 × 20 que ninguém quis. Quem chama converte os pixels de tela para o
 * mundo — a régua do dedo é a tela, não o desenho.
 *
 * Devolve `null` quando não há ponto legível ou quando o traço é curto demais
 * para valer como desenho. Traço reto ou aberto devolve o retângulo mínimo em
 * volta do traço.
 */
export function corrigirTracado(pontosCrus, { tolerancias: ajustes, extensaoMinima = 0 } = {}) {
  const tolerancias = ajustes ? { ...TOLERANCIAS, ...ajustes } : TOLERANCIAS;
  const limpos = limparPontos(pontosCrus);
  const caixa = caixaDePontos(limpos);
  if (!caixa) return null;
  if (extensaoMinima > 0 && Math.max(caixa.largura, caixa.altura) < extensaoMinima) return null;
  if (limpos.length < LIMITES_FORMA.PONTOS_MINIMO) return criarRetangulo(caixa);

  const diagonal = diagonalDe(caixa);
  if (diagonal < LIMITES_FORMA.LADO_MINIMO) return criarRetangulo(caixa);
  if (pareceElipse(limpos, tolerancias)) return criarElipse(caixa);

  const simplificados = semFechamentoRepetido(
    simplificar(limpos, diagonal * tolerancias.SIMPLIFICACAO),
    diagonal * tolerancias.SIMPLIFICACAO
  );
  const cantos = endireitar(simplificados, { tolerancias, diagonal });
  if (cantos.length < LIMITES_FORMA.PONTOS_MINIMO) return criarRetangulo(caixa);
  if (pareceRetangulo(cantos, tolerancias.ENCAIXE_GRAUS)) return criarRetangulo(caixaDePontos(cantos));
  return criarPoligono(cantos);
}

// Gesto que voltou ao começo deixa um último ponto em cima do primeiro; ele
// viraria um lado de 2 px e estragaria o fechamento.
function semFechamentoRepetido(pontos, tolerancia) {
  let cortados = [...pontos];
  while (cortados.length > LIMITES_FORMA.PONTOS_MINIMO && distancia(cortados[0], cortados[cortados.length - 1]) <= tolerancia) {
    cortados = cortados.slice(0, -1);
  }
  return cortados;
}
